import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * QR Loyalty — phone entry with a regional country-code selector.
 *
 * Regional countries (fixed order) let the guest type just their national
 * number; we assemble E.164 (+<dial><number>) and drop any trunk 0. "Other"
 * switches to a single field where an expat types their full +… number.
 * Emits (e164, isValid) on every change; parent stores the E.164 string.
 * Default country auto-detected from timezone → locale region → Jordan.
 */

const COUNTRIES = [
  { iso: "JO", dial: "962", flag: "🇯🇴", tz: ["Asia/Amman"] },
  { iso: "SA", dial: "966", flag: "🇸🇦", tz: ["Asia/Riyadh"] },
  { iso: "AE", dial: "971", flag: "🇦🇪", tz: ["Asia/Dubai", "Asia/Abu_Dhabi"] },
  { iso: "KW", dial: "965", flag: "🇰🇼", tz: ["Asia/Kuwait"] },
  { iso: "QA", dial: "974", flag: "🇶🇦", tz: ["Asia/Qatar"] },
  { iso: "BH", dial: "973", flag: "🇧🇭", tz: ["Asia/Bahrain"] },
  { iso: "OM", dial: "968", flag: "🇴🇲", tz: ["Asia/Muscat"] },
  { iso: "EG", dial: "20", flag: "🇪🇬", tz: ["Africa/Cairo"] },
];
const OTHER = "OTHER";
const E164_RE = /^\+[1-9]\d{6,14}$/;
const byIso = (iso) => COUNTRIES.find((c) => c.iso === iso);

function detectIso() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hit = COUNTRIES.find((c) => c.tz.includes(tz));
    if (hit) return hit.iso;
  } catch (_) {}
  try {
    const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const m = /[-_]([A-Za-z]{2})(?:$|[-_])/.exec(l || "");
      if (m && byIso(m[1].toUpperCase())) return m[1].toUpperCase();
    }
  } catch (_) {}
  return "JO";
}

// Regional: national digits, trunk 0 stripped, prefixed with the dial code.
//
// The guest often types the country code as well as picking it ("+962 79…",
// "0096279…", "96279…"). Prepending on top of that produced a 15-digit value
// like +962962775822111 which still passes E164_RE, so it was stored as a
// valid-looking wrong number and forked the customer record. Drop one leading
// copy of the dial code before prefixing, then re-strip a trunk zero the code
// was hiding. The backend applies the same rule authoritatively
// (PhoneNormalizer::collapseDoubledDialCode) — this keeps the value the guest
// SEES honest and covers clients that never reach it.
function assembleRegional(dial, national) {
  let d = String(national || "").replace(/\D/g, "").replace(/^0+/, "");
  if (d.startsWith(dial) && d.length - dial.length >= 7) {
    d = d.slice(dial.length).replace(/^0+/, "");
  }
  return d ? `+${dial}${d}` : "";
}
// Other: whatever they typed, coerced to +digits.
function assembleOther(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  return d ? `+${d}` : "";
}

// Parse a stored E.164 back into (iso, raw) for prefill / "not me" re-edit.
function parseE164(e164) {
  if (!e164 || !String(e164).startsWith("+")) return null;
  const digits = String(e164).slice(1).replace(/\D/g, "");
  const match = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length).find((c) => digits.startsWith(c.dial));
  if (match) return { iso: match.iso, raw: digits.slice(match.dial.length) };
  return { iso: OTHER, raw: `+${digits}` };
}

export default function PhoneField({ initialE164, onChange, invalid }) {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage || i18n.language || "en").startsWith("ar");

  const init = useMemo(() => parseE164(initialE164) || { iso: detectIso(), raw: "" }, [initialE164]);
  const [iso, setIso] = useState(init.iso);
  const [raw, setRaw] = useState(init.raw);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const isOther = iso === OTHER;
  const country = isOther ? null : byIso(iso) || COUNTRIES[0];

  // Emit E.164 + validity upward whenever the value changes.
  useEffect(() => {
    const e164 = isOther ? assembleOther(raw) : assembleRegional(country.dial, raw);
    onChange?.(e164, E164_RE.test(e164));
  }, [iso, raw, isOther, country, onChange]);

  // Close the menu on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const pick = (nextIso) => {
    setIso(nextIso);
    setRaw(""); // clear so a KW national number can't linger as a JO one
    setOpen(false);
  };

  const countryName = (c) => t(`scan_stay.country.${c.iso}`);

  return (
    <div className="ss-field">
      <label htmlFor="ss-phone">{t("scan_stay.phone_label")}</label>
      <div className="ss-phone" ref={wrapRef}>
        {/* ONE bordered bar; button + divider + input are borderless children */}
        <div className={`ss-phone-bar${invalid ? " invalid" : ""}`}>
          <button
            type="button"
            className="ss-phone-country"
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            {isOther ? <span>🌐</span> : <span>{country.flag}</span>}
            <span className="dial">{isOther ? t("scan_stay.other_short") : `+${country.dial}`}</span>
            <span className="caret" aria-hidden="true">▾</span>
          </button>

          <span className="ss-phone-divider" aria-hidden="true" />

          <input
            id="ss-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            className="ss-phone-input"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={isOther ? t("scan_stay.phone_ph_other") : t("scan_stay.phone_ph")}
            dir="ltr"
          />
        </div>

        {open && (
          <ul className="ss-phone-menu" role="listbox">
            {COUNTRIES.map((c) => (
              <li key={c.iso}>
                <button type="button" role="option" aria-selected={iso === c.iso} onClick={() => pick(c.iso)}>
                  <span className="flag">{c.flag}</span>
                  <span className="cname">{countryName(c)}</span>
                  <span className="cdial">+{c.dial}</span>
                </button>
              </li>
            ))}
            <li className="ss-phone-other">
              <button type="button" role="option" aria-selected={isOther} onClick={() => pick(OTHER)}>
                <span className="flag">🌐</span>
                <span className="cname">{t("scan_stay.other")}</span>
              </button>
            </li>
          </ul>
        )}
      </div>
      {isOther && <div className="ss-phone-hint">{t("scan_stay.other_hint")}</div>}
    </div>
  );
}
